// player-view orchestrator (SP1). Builds the DS-based player shell, wires the
// bw-* components to player.ts, starts a single rAF loop, kicks off audio load.
// Mounted behind ?pv flag from main.ts; legacy ui/player-panel.ts untouched.
//
// mountPlayerView — выбор экрана по живому порогу 768 px: телефонный экран песни
// (song-screen/phone-screen.ts) или широкая раскладка (mountWideView ниже).

import '@bandway/ui-components';
import { getState, setState } from '../state.js';
// DS component stylesheets — bw-* are light-DOM components that rely on these
// EXTERNAL CSS files (the desktop links them all in index.html). Without them
// the components render as unstyled plain HTML. Import the full set we use.
import '@bandway/ui-components/styles/base.css';
import '@bandway/ui-components/styles/bw-button.css';
import '@bandway/ui-components/styles/bw-play-button.css';
import '@bandway/ui-components/styles/bw-rec-button.css';
import '@bandway/ui-components/styles/bw-volume-bar.css';
import '@bandway/ui-components/styles/bw-knob.css';
import '@bandway/ui-components/styles/bw-vu-meter.css';
import '@bandway/ui-components/styles/bw-dropdown.css';
import '@bandway/ui-components/styles/bw-song-header.css';
import '@bandway/ui-components/styles/bw-transport.css';
import '@bandway/ui-components/styles/bw-track-cell.css';
import type { ShareManifest, SummaryPeaks, PerTrackSummaryPeaks } from '@bandway/share-types';
import type { Player } from '../player.js';
import './player-view.css';
// Chart & Lyrics stylesheet. Same trap as the bw-* files above: nothing else in
// the app imports it, so without this line the panel and the full-screen layer
// render as unstyled plain HTML.
import '../styles/chart.css';
import { mountSongHeader } from './song-header.js';
import { mountTransport } from './transport.js';
import { mountTrackCell, type TrackCellHandle } from './track-cell.js';
import { mountWaves } from './waves.js';
import { getViewMemo, setViewMemo } from './view-memo.js';
import { createVuLoop } from './vu-loop.js';
import type { ChartPanelDeps } from './chart-panel.js';
import { createChartSurfaces, type ChartSurfaces, type ChartShared } from './chart-surfaces.js';
import { setlistNav } from './setlist-nav.js';
import { textButton } from '../chart/chart-buttons.js';
import { watchPhoneMode } from './phone-mode.js';
import { mountPhoneScreen } from '../song-screen/phone-screen.js';
import { t } from '../ui/i18n.js';


export interface PlayerViewDeps {
  manifest: ShareManifest;
  peaks: SummaryPeaks;
  player: Player;
  perTrackPeaks: PerTrackSummaryPeaks | null;
  audioContext: AudioContext;
  /** Navigate to another song in the setlist (loadSong). Wired to the transport
   *  prev/next buttons + the rail. Absent in tests / single-share contexts. */
  onNavigate?: (shareId: string) => void;
  /** Mobile background audio: called synchronously from the Play tap to start
   *  the background <audio> inside the user gesture (and drive the live engine).
   *  When absent, the transport falls back to player.play() directly. */
  onPlayGesture?: () => void;
  /**
   * Шов ведения. В проде — настоящий createChartFollow; подменяется только в
   * тестах, и не ради изоляции, а потому что иначе двухстрочная проводка
   * bpm/timeSignature из манифеста не проверяется ничем: happy-dom раскладку не
   * считает, настоящее ведение выходит на «ширины нет», и мутация «не
   * передавать timeSignature» проходила зелёной по всем 1607 тестам
   * (мутационная проверка 03.09).
   */
  createFollow?: ChartPanelDeps['createFollow'];
  /**
   * Единый сетлист, фаза 3, задача D3 — «Version ▾» встаёт в ту же правую
   * группу шапки, что несёт Chart (`.pv-song-actions`), поэтому группа
   * заводится БЕЗУСЛОВНО (не только у песен с чартом — см. правку ниже), а
   * место под версии — этот шов. Вызывается РОВНО один раз на монтаж, до
   * кнопки Chart (порядок в DOM — «версии, затем Chart», как в design-gate
   * стенде, dev-stand/unified-setlist.ts). Отсутствует в тестах/одиночном
   * шейре вне сетлиста — там песня не имеет версий вовсе.
   */
  mountVersionActions?: (host: HTMLElement) => { unmount(): void };
}

export interface PlayerViewHandle { unmount: () => void; }

/**
 * Широкая раскладка (от 768 px) — сегодняшний плеер, та же схема, что в
 * десктоп-приложении. Телефонный экран — song-screen/phone-screen.ts; выбирает
 * между ними mountPlayerView внизу файла.
 */
function mountWideView(host: Element, deps: PlayerViewDeps, chartShared?: ChartShared): PlayerViewHandle {
  const { manifest, player, perTrackPeaks } = deps;
  host.textContent = '';
  // Телефонный экран не опускает флаг при размонтаже (смена песни не должна вспыхивать каркасом) —
  // широкой раскладке каркас нужен, и флаг опускает она.
  document.body.removeAttribute('data-pv-phone');

  const root = document.createElement('div');
  root.className = 'pv-root';

  // Header spans the top, full width — song identity only. Back-to-library
  // moved to the bottom of the setlist rail (rail-library-button.ts) to
  // declutter the header, especially on mobile.
  const headerSlot = document.createElement('div'); headerSlot.className = 'pv-header';
  const songheadSlot = document.createElement('div'); songheadSlot.className = 'pv-songhead';
  headerSlot.appendChild(songheadSlot);

  // Setlist position counter ("N / M") — signals the song is one of several so
  // the user knows there's more to navigate. Only in a multi-song setlist.
  // (Сергей 2026-07-02)
  const slItems = getState().setlistItems;
  const slIdx = slItems.findIndex((it) => it.shareId === manifest.shareId);
  if (slItems.length > 1 && slIdx >= 0) {
    const counter = document.createElement('div');
    counter.className = 'pv-song-counter';
    counter.textContent = `${slIdx + 1} / ${slItems.length}`;
    counter.setAttribute('aria-label', `Song ${slIdx + 1} of ${slItems.length}`);
    headerSlot.appendChild(counter);
  }

  // Правая группа шапки — веб-версия desktop `.song-actions` (см. CSS шапка
  // player-view.css `.pv-song-actions`). Заведена БЕЗУСЛОВНО (раньше жила
  // только внутри `hasChart` ниже): «Version ▾» песни без чарта — законное
  // состояние (слова могут быть пустыми, версии всё равно есть), и до этой
  // правки для такой песни не было места, куда его смонтировать (единый
  // сетлист, фаза 3, задача D3). Пустая группа (ни версий, ни чарта) — просто
  // пустой flex-контейнер, соседних правил не трогает.
  const actions = document.createElement('div');
  actions.className = 'pv-song-actions';
  headerSlot.appendChild(actions);
  const versionActionsHandle = deps.mountVersionActions?.(actions);

  // Top row mirrors desktop Playback: transport (left) + overview band (right).
  const topRow = document.createElement('div'); topRow.className = 'pv-top';
  const transportSlot = document.createElement('div'); transportSlot.className = 'pv-transport';
  const overviewSlot = document.createElement('div'); overviewSlot.className = 'pv-overview-col';
  topRow.appendChild(transportSlot);
  topRow.appendChild(overviewSlot);

  // Body — responsive grid of track cards (bw-track-cell, stage mode).
  const gridSlot = document.createElement('div'); gridSlot.className = 'pv-grid';

  // Row that holds the player column and, beside it, the sliding chart panel.
  // The panel SQUEEZES the mixer rather than covering it, so chart.css animates
  // its WIDTH (chart-panel.ts) — and that only works for a flex item in a ROW.
  // `.pv-root` is a column, hence this wrapper. Built BEFORE the column goes
  // into the host so `.pv-root` never has to be re-parented afterwards: moving
  // it would disconnect and reconnect every bw-* custom element inside it.
  const chartSplit = document.createElement('div');
  chartSplit.className = 'pv-chart-split';

  root.appendChild(headerSlot);
  root.appendChild(topRow);
  root.appendChild(gridSlot);
  chartSplit.appendChild(root);
  host.appendChild(chartSplit);

  // Prev/next setlist navigation for the transport buttons. Use the rail's
  // visual order (sorted by position) so "next" matches the row below. When
  // there's no adjacent song (single share, or first/last), the handler is
  // omitted → bw-transport keeps that button disabled.
  const nav = setlistNav(manifest.shareId);
  const prevShareId = nav.prevShareId;
  const nextShareId = nav.nextShareId;

  const header = mountSongHeader(songheadSlot, manifest);
  const transport = mountTransport(transportSlot, {
    player,
    durationSec: manifest.duration,
    onRecordAttempt: () => setState({ promoModal: { open: true, feature: 'recorder' } }),
    hasPrev: !!prevShareId,
    hasNext: !!nextShareId,
    onPrev: prevShareId ? () => deps.onNavigate?.(prevShareId) : undefined,
    onNext: nextShareId ? () => deps.onNavigate?.(nextShareId) : undefined,
    onPlayGesture: deps.onPlayGesture,
    // Master и петля общие с телефонным экраном (view-memo.ts): поворот пересобирает экран, а
    // плеер живёт дальше — стартуем с того, что выставили на прошлом, и пишем свои правки обратно.
    initialMaster: getViewMemo(player).master,
    onMasterChange: (f) => setViewMemo(player, { master: f }),
  });

  // Overview only — the Playback layout has no per-track wave lanes.
  const waves = mountWaves(overviewSlot, {
    manifest, peaks: deps.peaks, perTrackPeaks, player, showLanes: false,
    onLoopChange: (id) => setViewMemo(player, { loopSectionId: id }),
  });
  // Волна при монтаже сбросила петлю движка — вернуть ту, что стояла до пересборки (ПОСЛЕ сброса).
  waves.setLoop(getViewMemo(player).loopSectionId);

  const cells: TrackCellHandle[] = manifest.tracks.map((tr) => mountTrackCell(gridSlot, tr, player));

  // ── Chart & Lyrics ────────────────────────────────────────────────────────
  // Проводка поверхностей — chart-surfaces.ts (общая с телефонным экраном).
  // Здесь остаётся только кнопка Chart в шапке и панель, которая раздвигает микшер.
  // На телефоне панели нет вовсе: там свой экран со своим режимом Chart & Lyrics.
  let chartPanel: ReturnType<ChartSurfaces['mountPanel']> | null = null;

  // No chart text → no button, no panel, no layer. The manifest contract is
  // explicit that "no chart" and "no words" are ONE state for the reader and
  // never an empty surface (ShareManifest.chart) — and a missing field is what
  // every share published before this feature looks like.
  const surfaces = createChartSurfaces({
    manifest, player, nav,
    createFollow: deps.createFollow, onPlayGesture: deps.onPlayGesture, onNavigate: deps.onNavigate,
    shared: chartShared,
  });
  if (surfaces) {
    // Кнопка — ДС-компонент (chart/chart-buttons.ts), а не своя пилюля: пункт
    // приёмки A1-2, «кнопка должна быть кнопкой по дизайн-системе». Классы
    // остались, но красят ими больше НЕ: в player-view.css от них осталось
    // только место в шапке.
    //
    // Форма и размер — по живой приёмке (визуальная критика 16073e7c/c1b5fae0,
    // «Version ▾»): квадратный кап `md` — та же форма и высота, что у
    // desktop-кнопки «Chart» (app/renderer/ui/song-actions.js `.sa-chart-btn`,
    // `bw-button tone="default" size="md"`) и у чипов key/BPM самого
    // `bw-song-header` (тоже `size="md"`), — а не пилюля, подписанная полной
    // фразой. Подпись — короткая «Chart», как на десктопе; полная фраза
    // «Chart & Lyrics» остаётся подсказкой (`title`), ровно как у `.sa-chart-btn`
    // (`songActions.chart.tooltip`).
    const btn = textButton({
      text: t('chart.openLabel'),
      size: 'md',
      cls: 'pv-chart-btn',
      // Панель раскрыта или сложена — состояние ВНЕШНЕЕ, владелец у него
      // один (этот блок). Поэтому `selected`, а не aria-pressed руками:
      // атрибут выставляет сам bw-button, и двойного управления одним
      // атрибутом больше нет.
      onClick: () => {
        const next = !chartPanel?.isOpen();
        chartPanel?.setOpen(next);
        btn.selected = next;
      },
    });
    btn.title = t('chart.openTooltip');

    // Группа заведена выше (безусловно, до этого блока) — Chart встаёт
    // РЯДОМ с уже смонтированными версиями, не переразметая шапку.
    actions.appendChild(btn);
    chartPanel = surfaces.mountPanel(chartSplit, {
      phone: false,
      onClose: () => { chartPanel?.setOpen(false); btn.selected = false; },
    });

    // Restore reading (chart-surfaces.ts): the layer re-opens SYNCHRONOUSLY, in
    // the same task that built the mixer. Переход по сетлисту и смена экрана на
    // пороге 768 px рвут монтаж, а флаг чтения переживает — владелец у него state.ts.
    surfaces.restore(false);
  }

  const vuLoop = createVuLoop({ player, rows: cells, transport });
  vuLoop.start();

  // Mirror player events is handled by main.ts's state wiring; here we only
  // kick off audio load. main.ts also kicks load() for its error path; load()
  // memoizes the in-flight promise so both calls share ONE worker pool (B-260624).
  void player.load();

  return {
    unmount: () => {
      // Тему здесь не снимаем: её владелец переживает маршрут (theme-boot.ts),
      // и человек, ушедший из плеера, остаётся со своей темой.
      // First: both chart surfaces hold their own animation frames (the layer
      // its own rAF, the panel its follow loop) and would keep ticking a
      // detached DOM for the rest of the session. Флаг чтения (chartFullscreenOpen)
      // здесь НЕ опускается — его переживает и переход по сетлисту, и смена экрана.
      // Слой, панель и слот в body (его `host.textContent = ''` ниже не достаёт).
      surfaces?.destroy();
      versionActionsHandle?.unmount();
      vuLoop.stop();
      waves.unmount();
      for (const c of cells) c.unmount();
      transport.unmount();
      header.unmount();
      host.textContent = '';
    },
  };
}

/**
 * Экран песни по живому порогу PHONE_MAX (767 px, phone-mode.ts): до него —
 * телефонный экран (часть 1а редизайна), от 768 — сегодняшняя раскладка, та же
 * схема, что в десктоп-приложении (решение Сергея 16.10, «единый опыт»).
 * Переход через порог (поворот, ресайз) снимает один экран и ставит другой;
 * плеер, звук и флаг чтения переживают это — их владелец не экран.
 */
export function mountPlayerView(host: Element, deps: PlayerViewDeps): PlayerViewHandle {
  let view: PlayerViewHandle | null = null;
  // Тональность и колонки чарта — свойства ПЕСНИ: живут ровно один монтаж и
  // переходят от экрана к экрану (у каждого — свои поверхности, chart-surfaces.ts).
  const chartShared: ChartShared = { shift: 0, columns: 1 };
  const mountFor = (phone: boolean): void => {
    // СНАЧАЛА снять, потом ставить: общий хост принадлежит тому экрану, что стоит последним, а
    // старый, снятый позже нового, стёр бы чужое. Флаг body[data-pv-phone] поднимает телефонный
    // экран на монтаже, а опускает следующий за ним показывающий каркас (широкая раскладка).
    view?.unmount();
    view = null;
    view = phone ? mountPhoneScreen(host, deps, { chartShared }) : mountWideView(host, deps, chartShared);
  };
  const watch = watchPhoneMode(mountFor);
  mountFor(watch.isPhone());
  return {
    unmount: () => {
      watch.stop();
      view?.unmount();
      view = null;
    },
  };
}
